import { useState } from "react"
import { Link } from "react-router"
import {
  BookOpen, Monitor, ChevronDown, Lightbulb,
  CircleCheck, ArrowRight, Sun, Check, Circle,
  Trophy, Activity, AlertTriangle, Sparkles,
  Target, Shield, Zap,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { Separator } from "@/components/ui/separator"
import { Skeleton } from "@/components/ui/skeleton"
import { formatRelativeTime, normalizeStatus, statusLabel } from "../overview/helpers"
import type {
  EarnedMilestone, ProgressMilestone, MilestonesTeaser,
  SeasonalReadiness, EarnedGroup, GuidebookAccessEvent, OverviewPayload,
  MilestoneCategory,
} from "../overview/types"

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const LIFETIME_CATEGORIES = new Set<MilestoneCategory>(["prestige", "experience"])
const ROLLING_CATEGORIES = new Set<MilestoneCategory>(["adoption", "operational"])
const TIER_LABELS = ["", "I", "II", "III"]

const CATEGORY_META: Record<string, { label: string; icon: typeof Trophy; description: string }> = {
  prestige: {
    label: "Host Prestige",
    icon: Trophy,
    description: "Depth of hosting experience — countries, stays, guest-nights.",
  },
  adoption: {
    label: "Platform Adoption",
    icon: Zap,
    description: "How actively you use Stayframe surfaces — guidebook, display, content.",
  },
  experience: {
    label: "Guest Experience",
    icon: Target,
    description: "Guest satisfaction signals — reviews, ratings, rebooking intent.",
  },
  operational: {
    label: "Operational Excellence",
    icon: Shield,
    description: "Consistency and reliability — sync uptime, calendar accuracy.",
  },
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function tierDisplayLabel(tier: number): string {
  return TIER_LABELS[tier] || ""
}

function categoryLabel(cat: string): string {
  return CATEGORY_META[cat]?.label || cat
}

// ---------------------------------------------------------------------------
// PII Computation
// ---------------------------------------------------------------------------

export interface PiiResult {
  score: number
  tier: string
  tierLabel: string
  tone: "elite" | "strong" | "developing" | "early" | "inactive"
}

export interface CategoryStat {
  category: MilestoneCategory
  label: string
  icon: typeof Trophy
  description: string
  earned: number
  total: number
  pct: number
  isWeakest: boolean
  inProgress: ProgressMilestone[]
  earnedItems: EarnedMilestone[]
}

export function computePII(progress: {
  total_earned: number
  tier_breakdown: { t1: number; t2: number; t3: number }
  earned_groups: EarnedGroup[]
  in_progress: ProgressMilestone[]
}): PiiResult {
  const totalPossible = progress.total_earned + progress.in_progress.length
  if (totalPossible === 0) return { score: 0, tier: "inactive", tierLabel: "Inactive", tone: "inactive" }

  // Base score from milestone completion
  const completionPct = progress.total_earned / totalPossible

  // Tier weighting (higher tiers contribute more)
  const { t1, t2, t3 } = progress.tier_breakdown
  const tierScore = (t1 * 1 + t2 * 2 + t3 * 3) / (totalPossible * 3)

  // Category balance bonus
  const categories = new Map<string, { earned: number; total: number }>()
  for (const group of progress.earned_groups) {
    const existing = categories.get(group.category) || { earned: 0, total: 0 }
    existing.earned += group.items.length
    existing.total += group.items.length
    categories.set(group.category, existing)
  }
  for (const milestone of progress.in_progress) {
    const existing = categories.get(milestone.category) || { earned: 0, total: 0 }
    existing.total += 1
    categories.set(milestone.category, existing)
  }

  const catScores = Array.from(categories.values()).map(c => c.total > 0 ? c.earned / c.total : 0)
  const balanceBonus = catScores.length > 1
    ? 1 - (Math.max(...catScores) - Math.min(...catScores))
    : 0

  // Weighted composite: 60% completion, 25% tier quality, 15% balance
  const raw = (completionPct * 0.6 + tierScore * 0.25 + balanceBonus * 0.15) * 100
  const score = Math.min(100, Math.round(raw))

  if (score >= 86) return { score, tier: "elite", tierLabel: "Elite Operator", tone: "elite" }
  if (score >= 71) return { score, tier: "advanced", tierLabel: "Advanced", tone: "strong" }
  if (score >= 51) return { score, tier: "established", tierLabel: "Established", tone: "strong" }
  if (score >= 31) return { score, tier: "developing", tierLabel: "Developing", tone: "developing" }
  if (score >= 16) return { score, tier: "getting_started", tierLabel: "Getting Started", tone: "early" }
  return { score, tier: "inactive", tierLabel: "Inactive", tone: "inactive" }
}

export function buildCategoryStats(progress: {
  earned_groups: EarnedGroup[]
  in_progress: ProgressMilestone[]
}): CategoryStat[] {
  const allCategories = new Set<MilestoneCategory>([
    ...progress.earned_groups.map(g => g.category),
    ...progress.in_progress.map(m => m.category),
  ])

  const stats = Array.from(allCategories).map(cat => {
    const group = progress.earned_groups.find(g => g.category === cat)
    const earned = group?.items.length || 0
    const progressing = progress.in_progress.filter(m => m.category === cat)
    const total = earned + progressing.length
    const meta = CATEGORY_META[cat] || { label: cat, icon: Trophy, description: "" }

    return {
      category: cat,
      label: meta.label,
      icon: meta.icon,
      description: meta.description,
      earned,
      total,
      pct: total > 0 ? Math.round((earned / total) * 100) : 0,
      isWeakest: false,
      inProgress: progressing,
      earnedItems: group?.items || [],
    }
  })

  // Mark weakest category
  const withProgress = stats.filter(s => s.total > 0)
  if (withProgress.length > 0) {
    const weakest = withProgress.reduce((min, s) => s.pct < min.pct ? s : min, withProgress[0])
    const found = stats.find(s => s.category === weakest.category)
    if (found) found.isWeakest = true
  }

  return stats
}

export interface ProgressVerdict {
  headline: string
  summary: string
}

export function buildProgressVerdict(
  pii: PiiResult,
  categories: CategoryStat[],
  seasonal: SeasonalReadiness | null,
): ProgressVerdict {
  const weakest = categories.find(c => c.isWeakest)
  const weakestLabel = weakest?.label.toLowerCase() || "your next area"
  const allComplete = categories.every(c => c.pct === 100)

  if (allComplete) {
    return {
      headline: "Every milestone earned. You've reached full operational maturity.",
      summary: "All milestone categories are complete. Stay active to maintain rolling milestones.",
    }
  }

  if (pii.score >= 71) {
    return {
      headline: `Strong operational maturity, with room to push ${weakestLabel}.`,
      summary: weakest && weakest.pct < 80
        ? `${weakest.label} is at ${weakest.pct}% — closing the gap here would round out your profile.`
        : "Milestone progress is balanced across categories. Keep building toward Tier III milestones.",
    }
  }

  if (pii.score >= 51) {
    return {
      headline: `Established and building — ${weakestLabel} is the biggest opportunity.`,
      summary: weakest
        ? `${weakest.label} has the most room to grow at ${weakest.pct}%. Focus here for the fastest improvement.`
        : "Progress is spreading across categories. Next milestones are within reach.",
    }
  }

  if (pii.score >= 31) {
    const seasonalNote = seasonal && seasonal.detected_count < seasonal.total_count
      ? ` Seasonal readiness is ${seasonal.detected_count}/${seasonal.total_count}.`
      : ""
    return {
      headline: `Developing — the foundation is in place, ${weakestLabel} needs attention.`,
      summary: `Your milestones are growing but unevenly. ${weakest?.label || "One category"} is the clearest place to invest.${seasonalNote}`,
    }
  }

  if (pii.score >= 16) {
    return {
      headline: "Getting started — early milestones are landing.",
      summary: `Focus on the basics: keep your sync healthy, open the guidebook to guests, and let stays accumulate. Each one moves the score.`,
    }
  }

  return {
    headline: "Progress tracking is waiting for more data.",
    summary: "Milestones begin unlocking after your first few stays are synced. The intelligence index will sharpen as booking history builds.",
  }
}

// ---------------------------------------------------------------------------
// Intelligence Hero — dark editorial card (matches Performance/Reviews hero)
// ---------------------------------------------------------------------------

const TIER_LADDER = [
  { key: "inactive", label: "Inactive", min: 0 },
  { key: "getting_started", label: "Starting", min: 16 },
  { key: "developing", label: "Developing", min: 31 },
  { key: "established", label: "Established", min: 51 },
  { key: "advanced", label: "Advanced", min: 71 },
  { key: "elite", label: "Elite", min: 86 },
] as const

export function IntelligenceHero({
  pii,
  verdict,
  totalEarned,
  totalPossible,
  tierBreakdown,
}: {
  pii: PiiResult
  verdict: ProgressVerdict
  totalEarned: number
  totalPossible: number
  tierBreakdown: { t1: number; t2: number; t3: number }
}) {
  // Conditional gradient based on score range
  const gradientClass =
    pii.tone === "elite" ? "from-success/15 to-transparent"
    : pii.tone === "strong" ? "from-primary/12 to-transparent"
    : pii.tone === "developing" ? "from-amber-500/8 to-transparent"
    : pii.tone === "early" ? "from-warning/10 to-transparent"
    : "from-background/5 to-transparent"

  return (
    <section className="mb-8">
      <div className="rounded-xl bg-foreground text-background p-8 relative overflow-hidden">
        <div className={cn("absolute inset-0 bg-gradient-to-br pointer-events-none", gradientClass)} />

        <div className="relative">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-background/50" />
              <span className="text-[11px] font-medium uppercase tracking-wider text-background/50">
                Property Intelligence Index
              </span>
            </div>
            <span className="text-[11px] text-background/35">
              {totalEarned} of {totalPossible} milestones earned
            </span>
          </div>

          <div className="flex items-start gap-8">
            {/* PII Score */}
            <div className="flex-shrink-0 text-center min-w-[80px]">
              <p className={cn(
                "text-[52px] font-display font-semibold leading-none",
                pii.tone === "elite" && "text-success",
                pii.tone === "strong" && "text-background/95",
                pii.tone === "developing" && "text-warning/80",
                pii.tone === "early" && "text-warning/60",
                pii.tone === "inactive" && "text-background/40",
              )}>
                {pii.score}
              </p>
              <p className={cn(
                "text-[11px] font-semibold uppercase tracking-wider mt-1.5",
                pii.tone === "elite" && "text-success",
                pii.tone === "strong" && "text-primary",
                pii.tone === "developing" && "text-warning/70",
                pii.tone === "early" && "text-warning/50",
                pii.tone === "inactive" && "text-background/30",
              )}>
                {pii.tierLabel}
              </p>

              {/* Tier ladder — shows full scale so the label has context */}
              <div className="flex items-center gap-0.5 mt-3 justify-center">
                {TIER_LADDER.map((step) => {
                  const isActive = pii.tier === step.key
                  const isPast = pii.score >= step.min
                  return (
                    <div
                      key={step.key}
                      className={cn(
                        "h-1 rounded-full transition-all",
                        isActive ? "w-4 bg-primary" : "w-2",
                        !isActive && isPast && "bg-background/30",
                        !isActive && !isPast && "bg-background/10",
                      )}
                      title={step.label}
                    />
                  )
                })}
              </div>
            </div>

            {/* Verdict — Instrument Serif, larger */}
            <div className="flex-1">
              <h1 className="text-[22px] font-display font-medium leading-snug text-background/90 max-w-[92%]">
                {verdict.headline}
              </h1>
              <p className="text-[12px] text-background/45 mt-3 leading-relaxed">
                {verdict.summary}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Category Pillars — 4-column grid showing each progression layer
// ---------------------------------------------------------------------------

export function CategoryPillars({
  categories,
  inProgress,
}: {
  categories: CategoryStat[]
  inProgress: ProgressMilestone[]
}) {
  if (categories.length === 0) return null

  return (
    <section className="mb-8">
      <div className="flex items-center gap-2 mb-4">
        <Target className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          Progression Layers
        </span>
      </div>

      <div className={cn(
        "grid gap-4",
        categories.length >= 4 ? "grid-cols-2 lg:grid-cols-4" : `grid-cols-2 lg:grid-cols-${categories.length}`,
      )}>
        {categories.map((cat) => {
          const CatIcon = cat.icon
          const nearestMilestone = cat.inProgress
            .filter(m => m.target > 0)
            .sort((a, b) => (b.current / b.target) - (a.current / a.target))[0]

          return (
            <div
              key={cat.category}
              className={cn(
                "bg-card/80 backdrop-blur-sm border shadow-sm rounded-lg p-5 relative overflow-hidden transition-all duration-200",
                cat.isWeakest
                  ? "border-primary/30 ring-1 ring-primary/10"
                  : "border-border/50 hover:shadow-md hover:border-border",
              )}
            >
              {cat.isWeakest && (
                <div className="absolute top-0 left-0 w-1.5 h-full bg-primary rounded-l-lg" />
              )}

              <div className="flex items-center gap-2 mb-3">
                <CatIcon className={cn(
                  "h-3.5 w-3.5",
                  cat.isWeakest ? "text-primary" : "text-muted-foreground",
                )} />
                <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                  {cat.label}
                </span>
              </div>

              {/* Score */}
              <div className="flex items-baseline gap-1.5 mb-1">
                <span className="font-display text-3xl font-medium tracking-tight text-foreground tabular-nums">
                  {cat.pct}
                </span>
                <span className="text-sm text-muted-foreground/50">%</span>
              </div>
              <p className="text-[10px] text-muted-foreground/50 mb-3">
                {cat.earned}/{cat.total} milestones
              </p>

              {/* Progress bar */}
              <div className="h-1.5 bg-muted/80 rounded-full mb-3 overflow-hidden">
                <div
                  className={cn(
                    "h-1.5 rounded-full transition-all relative",
                    cat.pct >= 80 ? "bg-gradient-to-r from-primary/80 to-primary" : cat.pct >= 40 ? "bg-gradient-to-r from-primary/40 to-primary/70" : "bg-primary/30",
                  )}
                  style={{ width: `${cat.pct}%` }}
                >
                  {cat.pct >= 40 && (
                    <div className="absolute inset-0 bg-gradient-to-b from-white/15 to-transparent rounded-full" />
                  )}
                </div>
              </div>

              {/* Next milestone nudge */}
              {nearestMilestone ? (
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  {nearestMilestone.current >= nearestMilestone.target * 0.8
                    ? `${nearestMilestone.target - nearestMilestone.current} away from ${nearestMilestone.label}`
                    : `Working toward ${nearestMilestone.label} (${nearestMilestone.current}/${nearestMilestone.target})`
                  }
                </p>
              ) : cat.pct === 100 ? (
                <p className="text-[11px] text-primary">All milestones earned</p>
              ) : (
                <p className="text-[11px] text-muted-foreground/40">{cat.description}</p>
              )}

              {cat.isWeakest && cat.pct < 100 && (
                <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-border/30">
                  <span className="text-[9px] font-medium uppercase tracking-wider text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                    Biggest opportunity
                  </span>
                  <span className="text-[11px] text-primary font-medium flex items-center gap-1 cursor-default">
                    {cat.category === "adoption" && "Open your guidebook"}
                    {cat.category === "experience" && "View review insights"}
                    {cat.category === "prestige" && "Keep hosting"}
                    {cat.category === "operational" && "Check system status"}
                    <ArrowRight className="h-3 w-3" />
                  </span>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Focus Insight (deterministic rules engine, no LLM) — kept from original
// ---------------------------------------------------------------------------

interface Insight {
  text: string
  priority: number
}

function generateInsights(
  systemStatus: OverviewPayload["systemStatus"] | undefined,
  inProgress: ProgressMilestone[],
  seasonal: SeasonalReadiness | null,
): Insight[] {
  const insights: Insight[] = []

  // 1. Display offline — high urgency, easy fix
  if (systemStatus?.rows) {
    const displayRow = systemStatus.rows.find(r => r.key.includes("display"))
    if (displayRow && displayRow.configured !== false && normalizeStatus(displayRow.status) !== "healthy") {
      insights.push({
        text: `Your display has been offline${displayRow.last_run_at ? ` since ${formatRelativeTime(displayRow.last_run_at)}` : " for a while"}. Reconnecting it would improve your operational consistency — no content changes needed.`,
        priority: 1,
      })
    }
  }

  // 2. Strategy outdated
  if (systemStatus?.rows) {
    const stratRow = systemStatus.rows.find(r =>
      r.key.includes("strategy") || (r.key.includes("research") && normalizeStatus(r.status) === "warning"),
    )
    if (stratRow && normalizeStatus(stratRow.status) === "warning") {
      insights.push({
        text: "Your strategy is outdated. Regenerating it takes one click and keeps your content fresh.",
        priority: 2,
      })
    }
  }

  // 3. Near-miss milestones (>= 80% progress)
  const nearMiss = inProgress.find(m => m.target > 0 && (m.current / m.target) >= 0.8)
  if (nearMiss) {
    const remaining = nearMiss.target - nearMiss.current
    insights.push({
      text: `You're ${remaining} away from the next ${nearMiss.label} milestone (${nearMiss.target.toLocaleString()}). ${remaining <= 5 ? "This could happen with your next few bookings." : "Steady progress will get you there."}`,
      priority: remaining <= 5 ? 2 : 5,
    })
  }

  // 4. Seasonal readiness gap
  if (seasonal) {
    const unready = seasonal.signals.filter(s => !s.detected)
    if (unready.length > 0 && unready.length <= 3) {
      const labels = unready.map(s => s.label.toLowerCase()).join(" and ")
      insights.push({
        text: `For ${seasonal.season_label} readiness, you still need ${labels}. ${unready.length === 1 ? "This is the last step." : "Closing these gaps moves you to full seasonal readiness."}`,
        priority: 3,
      })
    }
  }

  return insights.sort((a, b) => a.priority - b.priority)
}

export function FocusInsightSection({
  systemStatus,
  inProgress,
  seasonal,
}: {
  systemStatus?: OverviewPayload["systemStatus"]
  inProgress: ProgressMilestone[]
  seasonal: SeasonalReadiness | null
}) {
  const insights = generateInsights(systemStatus, inProgress, seasonal)
  if (insights.length === 0) return null

  const topInsights = insights.slice(0, 2)

  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-3">
        <Lightbulb className="h-3.5 w-3.5 text-primary" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          Focus
        </span>
      </div>

      <div className="bg-card/80 backdrop-blur-sm border border-border/50 shadow-sm rounded-lg p-5 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-1.5 h-full bg-primary rounded-l-lg" />
        <div className="pl-3 space-y-3">
          {topInsights.map((insight, i) => (
            <p key={i} className="text-sm leading-relaxed text-foreground">
              {insight.text}
            </p>
          ))}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Seasonal Readiness + Nearest Milestones (compact, two-column)
// ---------------------------------------------------------------------------

export function NextStepsSection({
  seasonal,
  inProgress,
}: {
  seasonal: SeasonalReadiness | null
  inProgress: ProgressMilestone[]
}) {
  const nearestMilestones = inProgress.slice(0, 3)

  if (!seasonal && nearestMilestones.length === 0) return null

  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-4">
        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          What To Do Next
        </span>
      </div>

      <div className={cn("grid gap-4", seasonal ? "grid-cols-2" : "grid-cols-1")}>
        {/* Seasonal readiness */}
        {seasonal && (
          <div className="bg-card/80 backdrop-blur-sm border border-border/50 shadow-sm rounded-lg p-5">
            <div className="flex items-center gap-2 mb-3">
              <Sun className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs font-medium text-foreground">{seasonal.label}</span>
            </div>
            <div className="flex items-baseline gap-1.5 mb-4">
              <span className="font-display text-2xl font-medium text-foreground">
                {seasonal.detected_count}/{seasonal.total_count}
              </span>
              <span className="text-xs text-muted-foreground">ready</span>
            </div>
            <div className="space-y-2">
              {seasonal.signals.map((s) => (
                <div key={s.label} className="flex items-center gap-2">
                  {s.detected ? (
                    <Check className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                  ) : (
                    <Circle className="h-3.5 w-3.5 text-muted-foreground/30 flex-shrink-0" />
                  )}
                  <span
                    className={cn(
                      "text-xs",
                      s.detected ? "text-foreground" : "text-muted-foreground/50",
                    )}
                  >
                    {s.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Nearest milestones */}
        {nearestMilestones.length > 0 && (() => {
          const completed = nearestMilestones.filter(m => m.target > 0 && m.current >= m.target).length
          const overallPct = nearestMilestones.length > 0
            ? nearestMilestones.reduce((sum, m) => sum + (m.target > 0 ? Math.min(m.current / m.target, 1) : 0), 0) / nearestMilestones.length * 100
            : 0
          return (
            <div className="bg-card/80 backdrop-blur-sm border border-border/50 shadow-sm rounded-lg p-5 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-primary/30 rounded-l-lg" />
              <div className="pl-3">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-foreground">
                    Nearest Milestones
                  </span>
                  <span className="text-[10px] text-muted-foreground/50">
                    {completed}/{nearestMilestones.length} reached
                  </span>
                </div>
                <div className="h-1 bg-muted rounded-full mb-4">
                  <div
                    className="h-1 bg-primary/60 rounded-full"
                    style={{ width: `${overallPct}%` }}
                  />
                </div>
                <div className="space-y-2">
                  {nearestMilestones.map((m) => {
                    const pct = m.target > 0 ? Math.min(Math.round((m.current / m.target) * 100), 100) : 0
                    return (
                      <div key={m.id} className="flex items-center gap-2">
                        {pct >= 100 ? (
                          <Check className="h-3 w-3 text-primary flex-shrink-0" />
                        ) : (
                          <Circle className="h-3 w-3 text-muted-foreground/30 flex-shrink-0" />
                        )}
                        <span className={cn(
                          "text-xs flex-1",
                          pct >= 100 ? "text-foreground" : "text-muted-foreground/50",
                        )}>
                          {m.label}
                        </span>
                        <span className="text-[10px] text-muted-foreground/40 tabular-nums">
                          {m.current}/{m.target}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )
        })()}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// System Status (compact, kept from original)
// ---------------------------------------------------------------------------

export function OperationalStatusSection({
  systemStatus,
}: {
  systemStatus?: OverviewPayload["systemStatus"]
}) {
  const rows = systemStatus?.rows
  if (!rows || rows.length === 0) return null

  const visibleRows = rows.filter(r => r.configured !== false)
  if (visibleRows.length === 0) return null

  const failures = systemStatus?.failures

  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-4">
        <CircleCheck className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          System Status
        </span>
      </div>
      <div className="bg-card/80 backdrop-blur-sm border border-border/50 shadow-sm rounded-lg p-5">
        <div className="grid grid-cols-3 gap-4">
          {visibleRows.map((row) => {
            const status = normalizeStatus(row.status)
            return (
              <div key={row.key} className="flex items-start gap-2">
                <div
                  className={cn(
                    "w-2 h-2 rounded-full mt-0.5 flex-shrink-0",
                    status === "healthy" && "bg-primary",
                    status === "warning" && "bg-amber-400",
                    status === "error" && "bg-destructive",
                    status === "neutral" && "bg-muted-foreground/30",
                  )}
                />
                <div>
                  <p className="text-xs text-foreground">{row.label}</p>
                  <p
                    className={cn(
                      "text-[10px]",
                      status === "healthy" && "text-primary",
                      status === "warning" && "text-amber-500",
                      status === "error" && "text-destructive",
                      status === "neutral" && "text-muted-foreground/50",
                    )}
                  >
                    {row.detail || statusLabel(row.status)}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
        {failures && failures.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border/30">
            <span className="text-[10px] text-destructive">
              {failures.length} recent failure{failures.length !== 1 ? "s" : ""}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Milestone Detail — collapsible, below fold
// ---------------------------------------------------------------------------

export function MilestoneDetail({
  inProgress,
  earnedGroups,
  tierBreakdown,
}: {
  inProgress: ProgressMilestone[]
  earnedGroups: EarnedGroup[]
  tierBreakdown: { t1: number; t2: number; t3: number }
}) {
  const [expanded, setExpanded] = useState(false)
  const lifetimeMilestones = inProgress.filter(m => LIFETIME_CATEGORIES.has(m.category))
  const rollingMilestones = inProgress.filter(m => ROLLING_CATEGORIES.has(m.category))

  if (lifetimeMilestones.length === 0 && rollingMilestones.length === 0) return null

  return (
    <div className="mb-8">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 mb-4 group w-full text-left"
      >
        <Trophy className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          Milestone Detail
        </span>
        <ChevronDown
          className={cn(
            "h-3 w-3 text-muted-foreground/40 transition-transform",
            expanded && "rotate-180",
          )}
        />
        <span className="text-[10px] text-muted-foreground/40 ml-auto">
          {lifetimeMilestones.length + rollingMilestones.length} in progress
        </span>
      </button>

      {expanded && (
        <div className="space-y-6">
          {/* Lifetime */}
          {lifetimeMilestones.length > 0 && (
            <div className="bg-card/80 backdrop-blur-sm border border-border/50 shadow-sm rounded-lg p-5">
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-4">Lifetime</p>
              <div className="divide-y divide-border/30">
                {lifetimeMilestones.map((m) => {
                  const pct = m.target > 0 ? Math.min(Math.round((m.current / m.target) * 100), 100) : 0
                  const isNearMiss = pct >= 80
                  const currentTier = m.tier > 1 ? m.tier - 1 : 0

                  return (
                    <div key={m.id} className="py-3 first:pt-0 last:pb-0">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-medium text-foreground">{m.label}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold tabular-nums text-foreground">
                            {m.current.toLocaleString()}
                          </span>
                          {currentTier > 0 && (
                            <span className="text-[10px] font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                              Tier {tierDisplayLabel(currentTier)}
                            </span>
                          )}
                          <span className={cn("text-xs", isNearMiss ? "text-foreground" : "text-muted-foreground/60")}>
                            &rarr; {m.target.toLocaleString()}
                          </span>
                        </div>
                      </div>
                      <div className="h-1.5 bg-muted/80 rounded-full overflow-hidden">
                        <div
                          className={cn(
                            "h-1.5 rounded-full relative",
                            isNearMiss ? "bg-gradient-to-r from-primary/80 to-primary" : "bg-primary/50",
                          )}
                          style={{ width: `${pct}%` }}
                        >
                          {isNearMiss && (
                            <div className="absolute inset-0 bg-gradient-to-b from-white/15 to-transparent rounded-full" />
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Rolling */}
          {rollingMilestones.length > 0 && (
            <div className="border border-dashed border-border/60 bg-card/40 rounded-lg p-5">
              <div className="flex items-center justify-between mb-4">
                <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Rolling · 90 Days</p>
                <span className="text-[10px] text-muted-foreground/40">Resets with inactivity</span>
              </div>
              <div className="divide-y divide-border/20">
                {rollingMilestones.map((m) => {
                  const pct = m.target > 0 ? Math.min(Math.round((m.current / m.target) * 100), 100) : 0
                  const currentTier = m.tier > 1 ? m.tier - 1 : 0

                  return (
                    <div key={m.id} className="py-3 first:pt-0 last:pb-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-foreground">{m.label}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold tabular-nums text-foreground">
                            {m.current}
                          </span>
                          <span className="text-[10px] text-muted-foreground">in 90d</span>
                          {currentTier > 0 && (
                            <span className="text-[10px] font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                              Tier {tierDisplayLabel(currentTier)}
                            </span>
                          )}
                          <span className="text-xs text-muted-foreground/60">
                            &rarr; {m.target}
                          </span>
                        </div>
                      </div>
                      <div className="h-1 bg-muted/60 rounded-full">
                        <div
                          className="h-1 bg-primary/25 rounded-full"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Earned groups summary */}
          {earnedGroups.length > 0 && (
            <div className="bg-card/80 backdrop-blur-sm border border-border/50 shadow-sm rounded-lg p-5">
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-4">Earned Milestones</p>
              <div className="space-y-3">
                {earnedGroups.map((group) => (
                  <div key={group.category}>
                    <p className="text-xs font-medium text-foreground mb-1.5">{group.label}</p>
                    <div className="ml-2 pl-3 border-l-2 border-border/30 space-y-1.5">
                      {group.items.map((m) => (
                        <div key={m.id} className="flex items-center gap-3">
                          <Check className="h-3 w-3 text-primary flex-shrink-0" />
                          <span className="text-xs text-foreground flex-1">{m.label}</span>
                          <span className="text-[10px] font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                            Tier {tierDisplayLabel(m.tier)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Loading / Error / Empty states
// ---------------------------------------------------------------------------

export function ProgressSkeleton() {
  return (
    <div className="px-8 py-6 max-w-5xl space-y-8">
      {/* Hero */}
      <Skeleton className="h-48 rounded-xl" />
      {/* Category pillars */}
      <div>
        <Skeleton className="h-4 w-36 mb-4" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Skeleton className="h-44 rounded-lg" />
          <Skeleton className="h-44 rounded-lg" />
          <Skeleton className="h-44 rounded-lg" />
          <Skeleton className="h-44 rounded-lg" />
        </div>
      </div>
      {/* Focus */}
      <div>
        <Skeleton className="h-4 w-16 mb-3" />
        <Skeleton className="h-20 rounded-lg" />
      </div>
      {/* System status */}
      <div>
        <Skeleton className="h-4 w-28 mb-4" />
        <Skeleton className="h-20 rounded-lg" />
      </div>
    </div>
  )
}

export function ProgressError({ error }: { error: string }) {
  return (
    <div className="px-8 py-6 max-w-5xl">
      <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">
        <AlertTriangle className="h-4 w-4 flex-shrink-0" />
        <span>{error}</span>
      </div>
    </div>
  )
}

export function ProgressEmpty() {
  return (
    <div className="px-8 py-6 max-w-5xl">
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Trophy className="h-8 w-8 text-muted-foreground/30 mb-4" />
        <p className="text-sm font-medium text-foreground mb-1">Progress</p>
        <p className="text-xs text-muted-foreground max-w-sm">
          Progress tracking begins after your first few stays are synced.
        </p>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ProgressTeaser — compact card for overview page (kept for backwards compat)
// ---------------------------------------------------------------------------

export function MilestoneChip({ milestone }: { milestone: EarnedMilestone }) {
  const isRecent =
    milestone.achieved_at &&
    Date.now() - new Date(milestone.achieved_at).getTime() < 7 * 24 * 60 * 60 * 1000

  return (
    <span
      className={`ms-chip ms-chip--tier-${milestone.tier}${isRecent ? " ms-chip--recent" : ""}${milestone.next_label ? " ms-chip--has-next" : ""}`}
    >
      <span className="ms-chip__label">{milestone.label}</span>
      {milestone.next_label && (
        <span className="ms-chip__next">Next: {milestone.next_label}</span>
      )}
    </span>
  )
}

export function ProgressTeaser({ data }: { data: MilestonesTeaser }) {
  if (!data?.data_sufficient) return null
  const { earned_recent, closest, total_earned } = data

  return (
    <div className="ms-progress">
      {earned_recent.length > 0 && (
        <div className="ms-progress__earned">
          {earned_recent.map((milestone) => (
            <MilestoneChip key={milestone.id} milestone={milestone} />
          ))}
        </div>
      )}
      {closest && closest.target > 0 && (
        <div className="ms-progress__next">
          <div className="ms-progress__bar">
            <div
              className="ms-progress__fill"
              style={{ width: `${Math.min((closest.current / closest.target) * 100, 100)}%` }}
            />
          </div>
          <p className="ms-progress__label">
            {closest.current}/{closest.target} {closest.label}
          </p>
        </div>
      )}
      <p className="ms-progress__total">{total_earned} milestone{total_earned !== 1 ? "s" : ""} earned</p>
    </div>
  )
}
